import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateCustomerTypeInput,
  CustomerType,
  PaginatedResult,
  SortDirection,
  UpdateCustomerTypeInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface CustomerTypeListParams {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const customerTypeKeys = {
  all: ['customer-types'] as const,
  list: (params: CustomerTypeListParams) => ['customer-types', 'list', params] as const,
};

export function useCustomerTypes(
  params: CustomerTypeListParams,
): UseQueryResult<PaginatedResult<CustomerType>> {
  return useQuery({
    queryKey: customerTypeKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<CustomerType>>(
        `/customer-types${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          isActive: params.isActive,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

/** Convenience hook for selects: returns the active customer types ordered by sortOrder. */
export function useCustomerTypeOptions(): UseQueryResult<CustomerType[]> {
  return useQuery({
    queryKey: ['customer-types', 'options'],
    queryFn: () => api.get<CustomerType[]>('/customer-types/options'),
  });
}

export function useCreateCustomerType(): UseMutationResult<
  CustomerType,
  unknown,
  CreateCustomerTypeInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerTypeInput) =>
      api.post<CustomerType>('/customer-types', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerTypeKeys.all });
    },
  });
}

export interface UpdateCustomerTypeArgs {
  id: string;
  input: UpdateCustomerTypeInput;
}

export function useUpdateCustomerType(): UseMutationResult<
  CustomerType,
  unknown,
  UpdateCustomerTypeArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateCustomerTypeArgs) =>
      api.patch<CustomerType>(`/customer-types/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerTypeKeys.all });
    },
  });
}

export function useDeleteCustomerType(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/customer-types/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerTypeKeys.all });
    },
  });
}
